/* eslint-disable promise/always-return */
/* eslint-disable promise/catch-or-return */
import ENDPOINTS from 'tradera-constants/endpoints';
import {
    axiosConfigs,
    axiosWithTokenRefresh,
    finalizeResponse
} from 'tradera-utils/api';
import categoryService from 'tradera-services/category-service';
import ThrottledAction from 'tradera-apps/syi/script/app_react/utils/throttled-action';
import syiActions from './syi-actions';
import feesActions from './fees-actions';
import {
    fetchForCategory,
    fetchCategoryAttributeSuggestions
} from './attributes-actions';

import campaignCodeActions from './campaign-code-actions';
import { globalStore } from 'tradera-state/configure-store';
import { getSyiSlice } from 'tradera-state/views/syi/selectors/syi';
import { Analytics } from 'packages';

const store = globalStore();

var throttleTimeout = 1000;
var throttledCategoriesRequest = new ThrottledAction(throttleTimeout, true);
var numberOfLevels = 4;

function loadCategories(
    shortDescription,
    categoryId,
    index,
    executeImmediately
) {
    var request = function() {
        store.dispatch({
            type: 'CATEGORIES_LOADING',
            index: index
        });
        categoryService
            .loadChildCategories(shortDescription, categoryId, index)
            .then(result => {
                store.dispatch({
                    type: 'CATEGORIES_LOADED',
                    category: result,
                    index: index
                });
                categoryService.prefetchChildren(
                    shortDescription,
                    result,
                    index + 1
                );
            });
    };
    if (executeImmediately) {
        request();
    } else {
        throttledCategoriesRequest.execute(request);
    }
}

function closeLevel(index) {
    store.dispatch({
        type: 'CLOSE_LEVEL',
        index: index
    });
}
function resetLevel(index) {
    store.dispatch({
        type: 'RESET_LEVEL',
        index: index
    });
}
function disableLevel(index) {
    store.dispatch({
        type: 'DISABLE_LEVEL',
        index: index
    });
}
function toggleLevelLabel(index, value) {
    store.dispatch({
        type: 'TOGGLE_LEVEL_LABEL',
        index: index,
        value: value
    });
}
function selectLevelCategory(category, index) {
    store.dispatch({
        type: 'SELECT_LEVEL_CATEGORY',
        index: index,
        category: category
    });
}

const closeAllLevels = () => {
    for (let i = 0; i < numberOfLevels; i++) {
        closeLevel(i);
    }
};

export const updateCategoryRestrictions = categoryId => async () => {
    if (!categoryId) {
        resetCategoryRestriction();
        return;
    }

    try {
        const url = ENDPOINTS.SELLING_CATEGORY_RESTRICTIONS + '/' + categoryId;
        const response = await axiosWithTokenRefresh()
            .get(url, {
                ...axiosConfigs.authenticated
            })
            .then(finalizeResponse());
        syiActions.setCategoryRestriction(response.data);
    } catch (error) {
        resetCategoryRestriction();
        throw error;
    }
};

const resetCategoryRestriction = () =>
    syiActions.setCategoryRestriction({
        displayPaymentOptions: '',
        paymentOptionGroups: []
    });

const setDraftCategory = (categoryId, categoryPath) => (dispatch, getState) => {
    var slice = getSyiSlice(getState());
    if (!categoryPath) {
        categoryPath = slice.category.levels
            .filter(level => level.selectedCategory)
            .map(level => level.selectedCategory);
    }
    dispatch({
        type: 'SET_DRAFT_CATEGORY',
        categoryId: categoryId,
        categoryPath: categoryPath
    });
    dispatch(campaignCodeActions.validateCampaignCode());
    dispatch(updateCategoryRestrictions(categoryId));
    dispatch(fetchForCategory(categoryId));
    dispatch(
        fetchCategoryAttributeSuggestions(
            categoryId,
            slice.shortDescription.shortDescription
        )
    );
    dispatch(feesActions.updateCommission());
    dispatch(feesActions.updateMaxCommission());
};

const resetDraftCategory = () => dispatch => {
    dispatch(setDraftCategory(0, []));
};

const loadTopCategories = shortDescription => dispatch => {
    dispatch({
        type: 'CATEGORIES_LOADING',
        index: 0
    });

    categoryService.loadTopCategories(shortDescription).then(result => {
        dispatch({
            type: 'CATEGORIES_LOADED',
            category: result,
            index: 0,
            shortDescriptionForRecommendations: shortDescription
        });
        categoryService.prefetchChildren(shortDescription, result, 1);
    });
};

const setPath = (shortDescription, categoryPath) => () => {
    if (!categoryPath || !categoryPath.length) {
        closeAllLevels();
        return;
    }
    for (let i = 0; i < categoryPath.length; i++) {
        closeLevel(i);
        selectLevelCategory(categoryPath[i], i);
        if (i < categoryPath.length - 1) {
            loadCategories(
                shortDescription,
                categoryPath[i].categoryId,
                i + 1,
                true
            );
        }
    }
    for (var i = categoryPath.length; i < numberOfLevels; i++) {
        toggleLevelLabel(i, false);
    }
};

const setPathFromDraft = () => (dispatch, getState) => {
    let shortDescription = getSyiSlice(getState()).shortDescription;
    let category = getSyiSlice(getState()).category;
    dispatch(
        setPath(shortDescription.shortDescription, category.draftCategoryPath)
    );
};

const reset = () => dispatch => {
    dispatch({
        type: 'RESET_CATEGORY'
    });
    dispatch(feesActions.updateCommission());
};

const openLevel = (shortDescription, index) => dispatch => {
    closeAllLevels();
    if (index === 0) {
        dispatch(loadTopCategories(shortDescription));
    }
    dispatch({
        type: 'OPEN_LEVEL',
        index: index
    });
};

const toggleCategories = categoryLevel => dispatch => {
    dispatch({
        type: 'TOGGLE_CATEGORIES',
        index: categoryLevel.index,
        value: !categoryLevel.categoriesVisible
    });
};

const selectCategory = (
    shortDescription,
    categoryLevel,
    category
) => dispatch => {
    if (category.isLeaf) {
        closeLevel(categoryLevel.index);
        selectLevelCategory(category, categoryLevel.index);
        for (let i = categoryLevel.index + 1; i < numberOfLevels; i++) {
            toggleLevelLabel(i, false);
        }
        dispatch(setDraftCategory(category.categoryId));
    } else {
        dispatch(resetDraftCategory());
        for (let i = categoryLevel.index + 1; i < numberOfLevels; i++) {
            resetLevel(i);
            disableLevel(i);
            toggleLevelLabel(i, true);
        }
        closeLevel(categoryLevel.index);
        selectLevelCategory(category, categoryLevel.index);
        loadCategories(
            shortDescription,
            category.categoryId,
            categoryLevel.index + 1,
            true
        );
        dispatch(openLevel(shortDescription, categoryLevel.index + 1));
    }
    Analytics.trackEvent({
        category: 'SYI',
        action: `Select Category - Level ${categoryLevel.index + 1}`,
        label: `Suggested Categories - ${categoryLevel.suggestions
            .map(x => x.categoryId)
            .join()}`,
        value: category.categoryId
    });
};

export default {
    setDraftCategory,
    loadTopCategories,
    closeAllLevels,
    openLevel,
    toggleCategories,
    selectCategory,
    setPath,
    setPathFromDraft,
    reset
};
